/**
 * Entity transforms shared by the parser, writer and editor.
 */

import { type Entity, newId, seedIds } from '../model.ts';

/**
 * Deep-copy an entity, giving it and every descendant a fresh id.
 *
 * Use this whenever a library part is instantiated. `structuredClone` alone
 * copies the ids too, and two footprints sharing pad ids silently fuse in the
 * netlist — which keys nodes by id — so two DIP14s become one 14-pad part and
 * signals bleed between unrelated pins.
 */
export function instantiate<T extends Entity>(entity: T): T {
  const copy = structuredClone(entity);
  reassignIds(copy);
  return copy;
}

function reassignIds(e: Entity): void {
  e.id = newId();
  if (e.kind === 'component') {
    for (const child of e.children) reassignIds(child);
  }
}

export interface IdRepair {
  /** Entities given a new id because theirs was duplicated or invalid. */
  repaired: number;
  /** Highest id seen, after repair. */
  highest: number;
}

/**
 * Take the id counter past everything already in these entities.
 *
 * Every load needs this, whatever else it needs: the counter lives in the
 * module and knows nothing about a document until it is asked, so without it
 * the next object drawn takes an id something loaded already has.
 *
 * Split out from {@link ensureUniqueIds} because the two have different
 * lifetimes. Seeding is a fact about this session and runs on every load
 * forever. Repairing duplicates is a fact about *old documents* and is a
 * migration — it stops running once a document has been through it. Running
 * both on every load was cheap enough not to notice and meant the repair could
 * never be retired, which is the whole reason a document now says which
 * version of the model it was written against.
 *
 * Returns the highest id seen.
 */
export function seedIdsFrom(entities: Entity[]): number {
  let highest = 0;
  const scan = (list: Entity[]): void => {
    for (const e of list) {
      if (Number.isFinite(e.id) && e.id > highest) highest = e.id;
      if (e.kind === 'component') scan(e.children);
    }
  };
  scan(entities);
  seedIds(highest);
  return highest;
}

/**
 * Guarantee every entity in a document has a unique, positive id.
 *
 * Connectivity is keyed by id, so two entities sharing one are treated as the
 * same object: two footprints with duplicated pad ids fuse into a single pad,
 * their nets merge, and the ratsnest sees one island where there are two and
 * draws nothing. That is silent and looks like a rendering fault rather than
 * corrupt data.
 *
 * Documents written before component instantiation reassigned child ids carry
 * exactly this defect. Reaching them is what the version 0 to 1 migration is
 * for; this is also called at the few places that build entities by copying
 * others, where the same collision can be made fresh.
 *
 * Seeds the counter as {@link seedIdsFrom} does, since it has to scan anyway.
 */
export function ensureUniqueIds(entities: Entity[]): IdRepair {
  // First pass: find the highest id, so replacements start above everything.
  let highest = seedIdsFrom(entities);

  const seen = new Set<number>();
  let repaired = 0;

  const fix = (list: Entity[]): void => {
    for (const e of list) {
      if (!Number.isFinite(e.id) || e.id <= 0 || seen.has(e.id)) {
        e.id = newId();
        repaired++;
      }
      seen.add(e.id);
      if (e.id > highest) highest = e.id;
      if (e.kind === 'component') fix(e.children);
    }
  };
  fix(entities);

  return { repaired, highest };
}

/** Translate an entity's geometry in place. */
export function translateEntity(e: Entity, dx: number, dy: number): void {
  if (dx === 0 && dy === 0) return;
  switch (e.kind) {
    case 'line':
      e.x1 += dx; e.y1 += dy; e.x2 += dx; e.y2 += dy;
      break;
    case 'arc':
    case 'spiral':
      e.cx += dx; e.cy += dy;
      break;
    case 'pad':
    case 'text':
      e.x += dx; e.y += dy;
      break;
    case 'fill':
      e.bounds.x1 += dx; e.bounds.x2 += dx;
      e.bounds.y1 += dy; e.bounds.y2 += dy;
      for (const v of e.vertices) { v.x += dx; v.y += dy; }
      break;
    case 'component':
      // Moving a component moves its origin; children stay origin-relative.
      e.x += dx; e.y += dy;
      break;
  }
}

/**
 * Make a component's children and bounds relative to its insertion point.
 *
 * The v6 format writes component children in the *same* absolute coordinate
 * space as the `EndComp:: InsertionPoint:`, because a library is itself a
 * drawing — the stock PCB.LIB lays its 91 footprints out side by side across
 * 21 inches. Symbols generated from the compact libraries, by contrast, are
 * already origin-relative.
 *
 * Normalising on load means every component in memory has origin-relative
 * children and an insertion point in `x`/`y`, whatever it came from, so the
 * renderer and editor need only one rule. {@link absolutiseComponent} undoes
 * it on write so the file round-trips byte-for-byte.
 */
export function relativiseComponent(
  children: Entity[],
  bounds: { x1: number; y1: number; x2: number; y2: number },
  originX: number,
  originY: number,
  rotation = 0,
): void {
  if (originX === 0 && originY === 0 && !rotation) return;
  // Absolute → local: undo the placement's translate, then its rotation. The
  // renderer re-applies the rotation, so without this a rotated component would
  // be turned twice (a 90° part landing at 180°).
  const map = (x: number, y: number): [number, number] =>
    rot90(x - originX, y - originY, -rotation);
  for (const c of children) mapEntity(c, map, -rotation);
  mapBounds(bounds, map);
}

/** Inverse of {@link relativiseComponent}, applied to a copy when writing. */
export function absolutiseComponent(
  children: Entity[],
  bounds: { x1: number; y1: number; x2: number; y2: number },
  originX: number,
  originY: number,
  rotation = 0,
): void {
  if (originX === 0 && originY === 0 && !rotation) return;
  const map = (x: number, y: number): [number, number] => {
    const [rx, ry] = rot90(x, y, rotation);
    return [rx + originX, ry + originY];
  };
  for (const c of children) mapEntity(c, map, rotation);
  mapBounds(bounds, map);
}

/** Rotate a point about the origin. Multiples of 90° are exact, so a load →
 *  save round-trip of a rotated component is byte-for-byte. */
function rot90(x: number, y: number, deg: number): [number, number] {
  const d = ((deg % 360) + 360) % 360;
  if (d === 0) return [x, y];
  if (d === 90) return [-y, x];
  if (d === 180) return [-x, -y];
  if (d === 270) return [y, -x];
  const r = (d * Math.PI) / 180;
  const c = Math.cos(r);
  const s = Math.sin(r);
  return [x * c - y * s, x * s + y * c];
}

const norm = (deg: number): number => ((deg % 360) + 360) % 360;

/** Apply a point map to an entity's geometry, and shift its angles by `drot`
 *  so its own orientation is expressed relative to the component. */
function mapEntity(e: Entity, m: (x: number, y: number) => [number, number], drot: number): void {
  switch (e.kind) {
    case 'line':
      [e.x1, e.y1] = m(e.x1, e.y1);
      [e.x2, e.y2] = m(e.x2, e.y2);
      break;
    case 'arc':
      [e.cx, e.cy] = m(e.cx, e.cy);
      // Shift only the start; `rotation` (the arc's own zero-point/axis) is part
      // of its shape and the renderer re-applies the component rotation once.
      if (drot) e.angle = norm(e.angle + drot);
      break;
    case 'spiral':
      [e.cx, e.cy] = m(e.cx, e.cy);
      if (drot) e.a1 = norm(e.a1 + drot);
      break;
    case 'pad':
    case 'text':
      [e.x, e.y] = m(e.x, e.y);
      if (drot) e.rotation = norm((e.rotation ?? 0) + drot);
      break;
    case 'fill':
      [e.bounds.x1, e.bounds.y1] = m(e.bounds.x1, e.bounds.y1);
      [e.bounds.x2, e.bounds.y2] = m(e.bounds.x2, e.bounds.y2);
      for (const v of e.vertices) [v.x, v.y] = m(v.x, v.y);
      break;
    case 'component':
      [e.x, e.y] = m(e.x, e.y);
      if (drot) e.rotation = norm((e.rotation ?? 0) + drot);
      break;
  }
}

/** Remap an AABB by transforming its two corners and re-fitting. */
function mapBounds(
  b: { x1: number; y1: number; x2: number; y2: number },
  m: (x: number, y: number) => [number, number],
): void {
  const [ax, ay] = m(b.x1, b.y1);
  const [bx, by] = m(b.x2, b.y2);
  b.x1 = Math.min(ax, bx); b.x2 = Math.max(ax, bx);
  b.y1 = Math.min(ay, by); b.y2 = Math.max(ay, by);
}

/** An angle brought back into 0–360. */
const normDeg = (a: number): number => ((a % 360) + 360) % 360;

/**
 * Turn one item through `deg` about a point, geometry and all.
 *
 * The selection commands could only ever turn *components*, by bumping the
 * rotation on the placement — so rotating a grabbed track, arc or pad quietly
 * did nothing, and a block containing both came apart. Everything here moves.
 */
export function rotateEntity(e: Entity, deg: number, cx: number, cy: number): void {
  const r = (deg * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  const pt = (x: number, y: number): { x: number; y: number } => {
    const dx = x - cx;
    const dy = y - cy;
    return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
  };

  switch (e.kind) {
    case 'line': {
      const a = pt(e.x1, e.y1);
      const b = pt(e.x2, e.y2);
      e.x1 = a.x; e.y1 = a.y; e.x2 = b.x; e.y2 = b.y;
      // The bulge is a chord ratio, so turning the segment leaves it alone.
      break;
    }
    case 'arc': {
      const c = pt(e.cx, e.cy);
      e.cx = c.x; e.cy = c.y;
      e.angle = normDeg(e.angle + deg);
      if (e.rotation !== undefined) e.rotation = normDeg(e.rotation + deg);
      break;
    }
    case 'spiral': {
      const c = pt(e.cx, e.cy);
      e.cx = c.x; e.cy = c.y;
      e.a1 = normDeg(e.a1 + deg);
      e.a2 = normDeg(e.a2 + deg);
      break;
    }
    case 'pad':
    case 'text': {
      const p = pt(e.x, e.y);
      e.x = p.x; e.y = p.y;
      e.rotation = normDeg((e.rotation ?? 0) + deg);
      // A pad's slot is a second point in the drawing's frame, so it turns too.
      if (e.kind === 'pad' && e.slot) {
        const s = pt(e.slot.x, e.slot.y);
        e.slot = { x: s.x, y: s.y };
      }
      break;
    }
    case 'fill': {
      for (const v of e.vertices) {
        const p = pt(v.x, v.y);
        v.x = p.x; v.y = p.y;
      }
      // The stored box is axis-aligned, so it is rebuilt rather than turned.
      if (e.vertices.length) {
        e.bounds = {
          x1: Math.min(...e.vertices.map((v) => v.x)),
          y1: Math.min(...e.vertices.map((v) => v.y)),
          x2: Math.max(...e.vertices.map((v) => v.x)),
          y2: Math.max(...e.vertices.map((v) => v.y)),
        };
      } else {
        const a = pt(e.bounds.x1, e.bounds.y1);
        const b = pt(e.bounds.x2, e.bounds.y2);
        e.bounds = {
          x1: Math.min(a.x, b.x), y1: Math.min(a.y, b.y),
          x2: Math.max(a.x, b.x), y2: Math.max(a.y, b.y),
        };
      }
      break;
    }
    case 'component': {
      const p = pt(e.x, e.y);
      e.x = p.x; e.y = p.y;
      // A placed part carries its own rotation, so the children come with it.
      e.rotation = normDeg((e.rotation ?? 0) + deg);
      break;
    }
  }
}

